import * as React from 'react';
import { connect } from 'react-redux';
import { PlayCircleOutlined } from '@ant-design/icons';
import FileUploader from '../file_uploader/FileUploader';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import { imageUploadValidation } from '../../utils/ImageUtils';
import { getFileLink } from '../../utils/FileAccessUtils';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Gallery of GalleryItem[] ({ fileName, fileId, fileType }). Read-only mode
// renders the protected-image thumbnail grid; editable mode (create/edit modal,
// finalized in Phase 3) uses the shared FileUploader.
const DeliverableGallery = ({ gallery = [], editable = false, onChange, userType }) => {
	const items = gallery || [];

	if (editable) {
		return (
			<FileUploader
				defaultFileList={items.map((item) => ({
					fileId: nullSafeGetOrElse('fileId', item, ''),
					fileName: nullSafeGetOrElse('fileName', item, ''),
				}))}
				multiple
				accept="image/*,video/*"
				buttonType="default"
				beforeUpload={imageUploadValidation}
				handleUploadSuccess={(basicFileList) =>
					onChange &&
					onChange(
						(basicFileList || []).map((file) => ({
							fileId: file.fileId,
							fileName: file.fileName,
							fileType: 'image',
						}))
					)
				}
			/>
		);
	}

	if (items.length === 0) {
		return <div className="footnoteregular text-character-disabled">No media</div>;
	}

	// Videos would render as broken image thumbnails, so split them out: images go
	// through the protected thumbnail grid, videos render as playable links.
	const isVideo = (item) => nullSafeGetOrElse('fileType', item, 'image') === 'video';
	const images = items.filter((item) => !isVideo(item));
	const videos = items.filter(isVideo);

	return (
		<div className="flex flex-col gap-3">
			{images.length > 0 ? (
				<ProtectedImageThumbnails
					style={{ cursor: 'pointer', gap: 8 }}
					imageWidth={104}
					imageHeight={104}
					// Same format the WO details page passes: "fileId/fileName" per image
					// (ProtectedImageThumbnails splits on '/'). Passing fileId alone produced
					// a malformed URL, so nothing rendered.
					photoStrings={images.map(
						(item) =>
							`${nullSafeGetOrElse('fileId', item, '')}/${nullSafeGetOrElse('fileName', item, '')}`
					)}
					role={userType}
					showNumberOfImages={images.length}
				/>
			) : null}
			{videos.length > 0 ? (
				<div className="flex flex-wrap gap-2">
					{videos.map((item) => (
						<a
							key={nullSafeGetOrElse('fileId', item, '')}
							href={getFileLink(
								nullSafeGetOrElse('fileId', item, ''),
								nullSafeGetOrElse('fileName', item, ''),
								userType
							)}
							target="_blank"
							rel="noopener noreferrer"
							className="bodyregular inline-flex items-center gap-1.5 rounded-[2px] border border-solid border-grey-5 px-2.5 py-1.5 text-character-secondary hover:text-character-primary"
						>
							<PlayCircleOutlined className="text-[14px]" />
							{nullSafeGetOrElse('fileName', item, 'Video')}
						</a>
					))}
				</div>
			) : null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	userType: state.session.userType,
});

export default connect(mapStateToProps)(DeliverableGallery);
